import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, MailX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MaskedLines, Reveal } from "../components/motion";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/title";

const inputCls =
  "rounded-none border-border bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus-visible:ring-1 focus-visible:ring-forest focus-visible:ring-offset-0";

export default function Unsubscribe() {
  usePageTitle("Unsubscribe — OneHarvestCo. Benefits");
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/public/unsubscribe", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError("That didn't go through. Check the address and try once more.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="unsubscribe-page">
      <section className="mx-auto max-w-3xl px-6 pb-28 pt-36 lg:px-10 lg:pt-44">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="unsubscribe-kicker">
          Email Preferences
        </p>
        <MaskedLines
          className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl"
          lines={["Unsubscribe."]}
        />

        {done ? (
          <Reveal>
            <div
              className="mt-10 border border-border bg-sand/40 p-8 lg:p-10"
              data-testid="unsubscribe-confirmation"
            >
              <MailX className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <p className="mt-5 text-base leading-relaxed text-ink/80">
                You&rsquo;ve been unsubscribed from OneHarvestCo. Benefits emails. It can take up to
                10 business days to take full effect.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70">
              Enter the email address you&rsquo;d like removed. Nothing else is needed.
            </p>
            <form
              onSubmit={submit}
              className="mt-10 border border-border bg-sand/40 p-8 lg:p-10"
              data-testid="unsubscribe-form"
            >
              <div className="space-y-2">
                <Label
                  htmlFor="unsubscribe-email"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50"
                >
                  Email Address
                </Label>
                <Input
                  id="unsubscribe-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="unsubscribe-email-input"
                />
              </div>
              {error ? (
                <p className="mt-4 text-sm text-terra" data-testid="unsubscribe-error">
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={sending}
                data-testid="unsubscribe-submit"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Unsubscribe
              </button>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
